import { inject, Injectable } from '@angular/core';
import { RootServicesModule } from './root-services.module';
import { environment } from '@app-environment';
import {
    IDrive,
    IFile,
    IFolder,
    IUser,
    IFilter,
    IUploadedFile,
    IEditFile,
    UserDriveCollection,
    ISearchResult
} from '@app-root-interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { EUploadState } from '@app-root-enums';
import { IUploadFileForm } from '../interfaces/upload-file-form.interface';
import { TinyHelper } from '../../shared/helper/tiny-helper';
import { NoFilesAvailableError } from '../exceptions';
import { CreateFolder, IProfile, ISignedURL, UpdateFolder } from '@scf-monorepo/interfaces';
import { SignedURLDto } from '@scf-monorepo/backend-lib/dtos';

@Injectable({
    providedIn: RootServicesModule
})
export class DocumentService {
    private readonly http = inject(HttpClient);

    public getAllDrives(): Observable<IDrive[]> {
        return this.http.get<IDrive[]>(`${environment.gatewayAPI}/drive/admin`);
    }

    public getDrive(profile: IProfile): Observable<UserDriveCollection> {
        return this.getUserDrives(profile).pipe(
            map((drives) => {
                const allDrives = this.splitDrives(drives);

                if (!allDrives[0].length && !allDrives[1].length) {
                    throw new NoFilesAvailableError();
                }

                return allDrives;
            }),
            switchMap(([userDrives, basicDrives]) =>
                forkJoin([
                    this.loadDrivesAndMerge(userDrives),
                    this.loadBasicDrivesAndMerge(basicDrives),
                    of([...userDrives, ...basicDrives].map((drive) => drive.id))
                ]).pipe(map(([drives, basicFolder, allDriveIds]) => ({ drives, basicFolder, allDriveIds })))
            )
        );
    }

    private getUserDrives(profile: IProfile): Observable<IDrive[]> {
        let params = new HttpParams();
        if (profile && profile.country) {
            params = params.append('country', profile.country.toUpperCase());
        }
        if (profile && profile.organization) {
            params = params.append('company', profile.organization.toUpperCase());
        }
        if (profile && profile.department) {
            params = params.append('orgUnit', profile.department.toUpperCase());
        }
        if (profile && profile.location) {
            params = params.append('location', profile.location.toUpperCase());
        }
        return this.http.get<IDrive[]>(`${environment.gatewayAPI}/drive`, {
            params
        });
    }

    private splitDrives(drives: IDrive[]): [IDrive[], IDrive[]] {
        return drives.reduce(
            (acc, drive) => {
                acc[drive.isBasic ? 1 : 0].push(drive);
                return acc;
            },
            [[], []]
        );
    }

    private loadDrivesAndMerge(drives: IDrive[]): Observable<IDrive> {
        if (!drives.length) {
            return of({
                folders: [],
                files: [],
                isDrive: true,
                id: TinyHelper.createTmpId('tmp-')
            });
        }

        return forkJoin(drives.map((drive) => this.loadDriveById(drive))).pipe(
            map((driveList) =>
                driveList.reduce((acc, drive) => {
                    if (!acc) {
                        return {
                            folders: drive.folders,
                            files: drive.files,
                            isDrive: true,
                            id: TinyHelper.createTmpId('tmp-') // we need to make a temporary id here, because the drive is saved with this id
                        };
                    }
                    acc.folders = [...acc.folders, ...drive.folders];
                    acc.files = [...acc.files, ...drive.files];
                    return acc;
                }, null)
            )
        );
    }

    private loadBasicDrivesAndMerge(drives: IDrive[]): Observable<IFolder> {
        if (!drives.length) {
            return of({ files: [], isDrive: false });
        }
        return forkJoin(drives.map((drive) => this.loadDriveById(drive))).pipe(
            map((driveList) =>
                driveList.reduce((acc, drive) => {
                    if (!acc) {
                        return {
                            files: drive.files,
                            isDrive: false
                        };
                    }
                    acc.files = [...acc.files, ...drive.files];
                    return acc;
                }, null)
            )
        );
    }

    private loadDriveById(drive: IDrive): Observable<IDrive> {
        return this.http.get<IDrive>(`${environment.gatewayAPI}/drive/${drive.id}`);
    }

    public selectDrive(driveId: string) {
        return this.http.get<IDrive>(`${environment.gatewayAPI}/drive/${driveId}`);
    }

    public getFolder(key: string, isAdmin: boolean, profile: IProfile): Observable<IFolder> {
        if (isAdmin) {
            return this.http.get<IFolder>(`${environment.gatewayAPI}/folder/${key}`);
        } else {
            let params = new HttpParams();
            params = params.append('orgUnit', profile.department);
            return this.http.get<IFolder>(`${environment.gatewayAPI}/folder/${key}`, { params });
        }
    }

    public getMissingFavourites(files: { [key: string]: IFile }, user: IUser): Observable<IFile[]> {
        if (user && user.favoritDocuments && user.favoritDocuments.length > 0) {
            const alreadyLoadedIds = Object.values(files).map((file) => file.favoriteId);
            const keys: string[] = user.favoritDocuments.filter((id: string) => !alreadyLoadedIds.includes(id));

            if (keys.length > 0) {
                return this.getFavourites(keys);
            }
        }

        return of([]);
    }

    public getFavourites(ids: string[]): Observable<IFile[]> {
        let params = new HttpParams();
        ids.map((key) => (params = params.append('favoriteId', key)));
        return this.http.get<IFile[]>(`${environment.gatewayAPI}/file/favorite`, {
            params
        });
    }

    public getRecentlySeen(keys: string[]): Observable<IFile[]> {
        let params = new HttpParams();
        keys.map((key) => (params = params.append('id', key)));
        return this.http.get<IFile[]>(`${environment.gatewayAPI}/file`, {
            params
        });
    }

    public searchFiles(keyword: string, filters: IFilter, driveIds?: string[]): Observable<IFile[]> {
        let paramCount = 0;
        if (keyword) {
            paramCount = 1;
        }
        paramCount += filters.languages.length + filters.formats.length + filters.orgs.length + filters.topics.length;

        if (paramCount <= 0) {
            return of([]);
        }

        const keywordQuery = keyword ? `(titles.text:*${this.escapeQuery(keyword)}*)` : '';
        const languageQuery = filters.languages.length > 0 ? `(languages: ${filters.languages.join(' OR ')})` : '';
        const orgsQuery = filters.orgs.length > 0 ? `(orgUnit: ${filters.orgs.join(' OR ')})` : '';
        const formatQuery =
            filters.formats.length > 0
                ? `(extension: ${this.mapFilterEntities(filters.formats)
                      .map((types) => `(${types.join(' OR ')})`)
                      .join(' OR ')})`
                : '';
        const driveIdQuery = driveIds?.length ? `(driveId: ${driveIds.join(' OR ')})` : '';

        const query = [keywordQuery, languageQuery, orgsQuery, formatQuery, driveIdQuery]
            .filter((param) => !!param && param !== '')
            .join(' AND ');

        const params = new HttpParams().append('query', query);
        return this.http
            .get<ISearchResult<IFile>>(`${environment.gatewayAPI}/search`, { params })
            .pipe(map((searchResult) => searchResult.content));
    }

    private escapeQuery(query: string): string {
        return query.replace(/[<>]/g, ``).replace(/([+-=&|><!(){}[\]^"~*?:\\/])/g, '\\$1');
    }

    public createFile(file: IUploadedFile): Observable<IUploadedFile> {
        return this.http.post<IFile>(`${environment.gatewayAPI}/drives/file`, file).pipe(
            map((newFile) => ({
                ...file,
                id: newFile.id,
                extension: newFile.extension,
                uploadState: EUploadState.FINISHED
            }))
        );
    }

    public createFolder(name: string, parent: IFolder, _drive: IDrive): Observable<IFolder> {
        const folder: IFolder = {
            files: [],
            folders: [],
            name
        };
        const newParent = { ...parent, folders: [...parent.folders] };
        newParent.folders.push(folder);
        const createFolderDto: CreateFolder = {
            name: folder.name
        };
        return this.http
            .post<IFolder>(`${environment.gatewayAPI}/folder?parentId=${newParent.id}`, createFolderDto)
            .pipe(
                map((updatedFolder) => {
                    if (parent.isDrive) {
                        return {
                            ...updatedFolder,
                            isDrive: true
                        };
                    }
                    return updatedFolder;
                })
            );
    }

    public getFile(id: string): Observable<IFile> {
        return this.http.get<IFile>(`${environment.gatewayAPI}/file/${id}`);
    }

    public deleteFolder(folder: IFolder): Observable<void> {
        return this.http.delete<void>(`${environment.gatewayAPI}/folder/${folder.id}`);
    }

    public patchFolder(folder: IFolder): Observable<IFolder> {
        const updateFolderDto: UpdateFolder = {
            name: folder.name
        };
        return this.http.patch<IFolder>(`${environment.gatewayAPI}/folder/${folder.id}`, updateFolderDto);
    }

    public deleteFile(file: IFile): Observable<void> {
        return this.http.delete<void>(`${environment.gatewayAPI}/file/${file.id}`);
    }

    public updateFile(file: IEditFile): Observable<IEditFile> {
        const changedFile = { ...file };
        if (changedFile.file) {
            changedFile.name = changedFile.file.name;
            changedFile.mimeType = changedFile.file.type;
            changedFile.extension = changedFile.file.name.substr(changedFile.file.name.lastIndexOf('.') + 1);
            changedFile.snippets = {};
        }
        return this.http
            .put<IFile>(`${environment.gatewayAPI}/drives/file/${file.id}`, changedFile)
            .pipe(map((data) => ({ ...file, ...data })));
    }

    public changeFile(file: IFile, payload: IUploadFileForm): Observable<IFile> {
        let orgUnits = [];
        const res: IFile = {
            ...file,
            metaInfo: {
                ...file.metaInfo,
                owner: payload.author,
                version: payload.version
            },
            parentFolders: [],
            titles: payload.titles,
            subtitle: payload.subtitle,
            languages: payload.language,
            parentDrive: file.parentDrive ? { ...file.parentDrive } : undefined
        };
        if (payload.parentDrive) {
            res.parentDrive.id = payload.parentDrive.id;
            orgUnits = [...payload.parentDrive.orgUnit];
        }
        if (payload.parentFolders) {
            res.parentFolders = payload.parentFolders.map((folder) => {
                folder.orgUnit.map((unit) => {
                    if (orgUnits.indexOf(unit) < 0) {
                        orgUnits.push(unit);
                    }
                });
                return {
                    ...folder,
                    orgUnit: folder.orgUnit
                };
            });
        }
        return of(res);
    }

    public updateEditFile(file: IEditFile, _drive: IFolder): Observable<IEditFile> {
        const payload = {
            id: file.id,
            name: file.file.name,
            mimeType: file.file.type,
            owner: file.updatedBy.name
        };
        return this.http.delete(`${environment.gatewayAPI}/uploads/files/${file.name}`).pipe(
            switchMap(() => this.http.post<{ signedUrl: string }>(`${environment.gatewayAPI}/uploads/files`, payload)),
            switchMap(({ signedUrl }) =>
                this.http
                    .get(file.file.url, { responseType: 'blob' as const })
                    .pipe(map((fileBlob) => ({ signedUrl, fileBlob })))
            ),
            switchMap(({ signedUrl, fileBlob }) => this.http.put<any>(signedUrl, fileBlob)),
            map(() => ({
                ...file,
                name: file.file.name
            })),
            catchError((err) => {
                console.error(err);
                return throwError(err);
            })
        );
    }

    public getFileByFavId(id: string): Observable<IFile> {
        return this.http.get<IFile>(`${environment.gatewayAPI}/file/favorite/${id}`);
    }

    public getPreview(fileId: string): Observable<Blob> {
        return this.getPreviewUrl(fileId).pipe(
            switchMap(({ signedURL }) => this.http.get(signedURL, { responseType: 'blob' }))
        );
    }

    public updateEditPreview(file: IEditFile, _drive: IFolder): Observable<IFile> {
        if (!file.preview) {
            return of(file);
        }
        const payload = {
            id: file.id,
            name: file.preview.name,
            mimeType: file.preview.type,
            owner: file.updatedBy.name
        };
        let observable: Observable<any>;
        if (file.metaInfo && file.metaInfo.thumbnail) {
            const previewName = file.metaInfo.thumbnail.substr(file.metaInfo.thumbnail.lastIndexOf('/') + 1);
            observable = this.http.delete(`${environment.gatewayAPI}/uploads/previews/${previewName}`);
        } else {
            observable = of({});
        }
        return observable.pipe(
            switchMap(() =>
                this.http.post<{ signedUrl: string; locationUrl: string }>(
                    `${environment.gatewayAPI}/uploads/previews`,
                    payload
                )
            ),
            switchMap(({ signedUrl, locationUrl }) =>
                this.http
                    .get(file.preview.url, { responseType: 'blob' as const })
                    .pipe(map((fileBlob) => ({ signedUrl, fileBlob, locationUrl })))
            ),
            switchMap(({ signedUrl, fileBlob, locationUrl }) =>
                this.http.put<IFile>(signedUrl, fileBlob).pipe(
                    map(() => ({
                        ...file,
                        metaInfo: { ...file.metaInfo, thumbnail: locationUrl }
                    }))
                )
            ),
            catchError((err) => {
                console.error(err);
                return throwError(err);
            })
        );
    }

    public deleteFileFromFolder(
        file: IFile,
        folder: IFolder,
        drive: IFolder
    ): Observable<{ file: IFile; folder: IFolder }> {
        const folderCopy: IFolder = { ...folder, files: [...folder.files] };
        const fileCopy: IFile = { ...file, parentDrive: { ...file.parentDrive } };
        if (folder.id === drive.id) {
            folderCopy.files = folderCopy.files.filter((copy) => copy.id !== file.id);
            if (fileCopy.parentDrive) {
                delete fileCopy.parentDrive;
            } else {
                return throwError('No parent drive');
            }
        } else {
            folderCopy.files = folderCopy.files.filter((copy) => copy.id !== file.id);
            if (fileCopy.parentFolders) {
                const newParents = fileCopy.parentFolders.filter((copy) => copy.id !== folder.id);
                if (newParents.length === fileCopy.parentFolders.length) {
                    return throwError('No parent folder');
                }
                fileCopy.parentFolders = newParents && newParents.length > 0 ? newParents : undefined;
            } else {
                return throwError('No parent folder');
            }
        }
        return this.http
            .put<IFile>(`${environment.gatewayAPI}/drives/file/${file.id}`, fileCopy)
            .pipe(map(() => ({ file: fileCopy, folder: folderCopy })));
    }

    private mapFilterEntities(entities: string[]): string[][] {
        return entities.map((entity) => {
            switch (entity) {
                case 'Excel Table':
                    return ['xls', 'xlt', 'xla', 'xlsx', 'xltx', 'xlsm', 'xltm', 'xlam', 'xlsb'];
                case 'Document':
                    return ['doc', 'docx', 'dot', 'dotx', 'docm', 'dotm'];
                case 'Power Point':
                    return ['ppt', 'pot', 'pps', 'ppa', 'pptx', 'potx', 'ppsx', 'ppam', 'pptm', 'potm', 'ppsm'];
                case 'PDF':
                    return ['pdf'];
                case 'Text File':
                    return ['txt'];
                case 'Zip-Archive':
                    return ['zip', '7z'];
                case 'HTM File':
                    return ['htm', 'html'];
                default:
                    return [entity];
            }
        }, []);
    }

    private getPreviewUrl(fileId: string): Observable<ISignedURL> {
        return this.http.get<SignedURLDto>(`${environment.gatewayAPI}/file/${fileId}/preview-url`);
    }
}
